import { AnalyticsIntegrations, fireAnalyticsEvent } from '../helpers/fireAnalyticsEvent'
import { PlanExtraDetail } from '@dtx-company/true-common/src/types/Plan'
import {
  fireBingProConversionEvent,
  fireGtmEvent,
  fireHeapEvent,
  fireOptimizelyEvent
} from '../helpers'
import { fireImpactTrackConversion } from '../helpers/impact'
import eventNames from '../constants/proConversion'

const events = {
  userStartedProConversion(args?: {
    email: string
    lockPrismicId?: string
    planType: string | undefined
    planSelected: string | undefined
    currentUserPlan: string | undefined
    codeLimit: number | undefined
    pageLimit: number | undefined
    conversionSource: string | undefined
    isLoggedIn: boolean
    conversionType: string
  }): void {
    const { userStartedProConversion } = eventNames
    fireGtmEvent(userStartedProConversion, { enhanced_conversion_data: { email: args?.email } })
    fireHeapEvent(userStartedProConversion, args)
  },
  userSubmittedProInfo(): void {
    const { userSubmittedProInfo } = eventNames
    fireGtmEvent(userSubmittedProInfo)
    fireHeapEvent(userSubmittedProInfo)
  },
  userSubmittedProPayment(): void {
    const { userSubmittedProPayment } = eventNames
    fireGtmEvent(userSubmittedProPayment)
    fireHeapEvent(userSubmittedProPayment)
  },
  userCompletedProConversion(args: {
    planSelected: string
    previousPlan: string | undefined
    promoCode: string | undefined
    cohort: string | undefined
    lockPrismicId?: string
    email: string
    planPrice: number | undefined
    conversionSource: string | undefined
    isLoggedIn: boolean
    conversionType: string | undefined
    planDetails: PlanExtraDetail[] | undefined
    paymentMethodType: string | undefined
    ithacaId: string
    subscriptionId: string | undefined
    couponDiscountInCents: number | undefined
  }): void {
    const { userCompletedProConversion } = eventNames
    const { ithacaId, subscriptionId, couponDiscountInCents, ...rest } = args
    fireGtmEvent(userCompletedProConversion, {
      enhanced_conversion_data: { email: args.email },
      ...(args.planPrice ? { value: args.planPrice / 100, currency: 'USD' } : undefined)
    })
    fireAnalyticsEvent(userCompletedProConversion, rest, [AnalyticsIntegrations.HEAP])
    fireOptimizelyEvent(userCompletedProConversion)
    fireBingProConversionEvent()
    fireImpactTrackConversion({
      currencyCode: 'USD',
      customerEmail: args.email,
      customerId: ithacaId,
      //we don't a have a truly unique id for the order
      orderId: `${subscriptionId ?? ''}-${args.planSelected}`,
      //needs to be in dollar amount
      orderDiscount: couponDiscountInCents ? (couponDiscountInCents * -1) / 100 : 0,
      orderPromoCode: args.promoCode ?? '',
      items: [
        {
          category: 'subscriptionPlan',
          quantity: 1,
          sku: args.planSelected,
          //needs to be in dollar amount
          subTotal: args.planPrice ? args.planPrice / 100 : 0
        }
      ]
    })
  },
  userCancelledPlan(): void {
    const { userCancelledPlan } = eventNames
    fireGtmEvent(userCancelledPlan)
    fireHeapEvent(userCancelledPlan)
  },
  userClickedGetMoreCodes(): void {
    const { userClickedGetMoreCodes } = eventNames
    fireHeapEvent(userClickedGetMoreCodes)
  },
  Revenue_Global_Clicked_UpgradeButton(args: {
    pathname: string
    codeLimit: number
    pageLimit: number
    currentUserPlan?: string
    type: string
  }): void {
    const { Revenue_Global_Clicked_UpgradeButton } = eventNames
    fireHeapEvent(Revenue_Global_Clicked_UpgradeButton, args)
  },
  Revenue_PaymentForm_Selected(args: { field: string; value?: number | string }): void {
    const { Revenue_PaymentForm_Selected } = eventNames
    fireHeapEvent(Revenue_PaymentForm_Selected, args)
  },
  Revenue_ProFlex_BillingTab_Reached(clicked: string): void {
    const { Revenue_ProFlex_BillingTab_Reached } = eventNames
    fireHeapEvent(Revenue_ProFlex_BillingTab_Reached, { clicked })
  },
  Revenue_Invoices_UserClicked(
    clicked: 'view-all' | 'download-all' | 'download-single' | 'preview-single'
  ): void {
    const { Revenue_Invoices_UserClicked } = eventNames
    fireHeapEvent(Revenue_Invoices_UserClicked, { clicked })
  },
  Revenue_DowngradeConfirmationModal_UserClicked(
    clicked: 'continue-downgrade' | 'cancel-downgrade'
  ): void {
    const { Revenue_DowngradeConfirmationModal_UserClicked } = eventNames
    fireHeapEvent(Revenue_DowngradeConfirmationModal_UserClicked, { clicked })
  },
  Flowcode_Homepage_Clicked_Signup_Google(): void {
    const { Flowcode_Homepage_Clicked_Signup_Google } = eventNames
    fireHeapEvent(Flowcode_Homepage_Clicked_Signup_Google)
  },
  Flowcode_Homepage_Clicked_Signup_Facebook(): void {
    const { Flowcode_Homepage_Clicked_Signup_Facebook } = eventNames
    fireHeapEvent(Flowcode_Homepage_Clicked_Signup_Facebook)
  },
  Flowcode_Management_Clicked_Dropdown_Download_Code(): void {
    const { Flowcode_Management_Clicked_Dropdown_Download_Code } = eventNames
    fireHeapEvent(Flowcode_Management_Clicked_Dropdown_Download_Code)
  },
  Flowcode_Management_Clicked_Settings_Dropdown(args: { menuItem: string }): void {
    const { Flowcode_Management_Clicked_Settings_Dropdown } = eventNames
    fireHeapEvent(Flowcode_Management_Clicked_Settings_Dropdown, args)
  },
  Flowcode_Management_Clicked_Delete_Modal(args: { action: string }): void {
    const { Flowcode_Management_Clicked_Delete_Modal } = eventNames
    fireHeapEvent(Flowcode_Management_Clicked_Delete_Modal, args)
  },
  Flowcode_Management_Clicked_Share_Dropdown(args: { menuItem: string }): void {
    const { Flowcode_Management_Clicked_Share_Dropdown } = eventNames
    fireHeapEvent(Flowcode_Management_Clicked_Share_Dropdown, args)
  },
  Flowcode_Management_Clicked_Copy_Link(args: { action: string }): void {
    const { Flowcode_Management_Clicked_Copy_Link } = eventNames
    fireHeapEvent(Flowcode_Management_Clicked_Copy_Link, args)
  },
  Flowcode_Management_Clicked_Add_To_Folder(): void {
    const { Flowcode_Management_Clicked_Add_To_Folder } = eventNames
    fireHeapEvent(Flowcode_Management_Clicked_Add_To_Folder)
  },
  Flowcode_Management_Clicked_Expand_Options(args: { menuItem: string }): void {
    const { Flowcode_Management_Clicked_Expand_Options } = eventNames
    fireHeapEvent(Flowcode_Management_Clicked_Expand_Options, args)
  },
  Flowcode_Management_Clicked_Learn_How_To_Use_Video(): void {
    const { Flowcode_Management_Clicked_Learn_How_To_Use_Video } = eventNames
    fireHeapEvent(Flowcode_Management_Clicked_Learn_How_To_Use_Video)
  },
  Flowcode_Management_Clicked_Help_Center(args: { menuItem: string }): void {
    const { Flowcode_Management_Clicked_Help_Center } = eventNames
    fireHeapEvent(Flowcode_Management_Clicked_Help_Center, args)
  },
  Flowcode_Management_Clicked_Email_Us(): void {
    const { Flowcode_Management_Clicked_Email_Us } = eventNames
    fireHeapEvent(Flowcode_Management_Clicked_Email_Us)
  },
  Flowcode_Management_Edit_Code_Typed_Flowcode_Name(): void {
    const { Flowcode_Management_Edit_Code_Typed_Flowcode_Name } = eventNames
    fireHeapEvent(Flowcode_Management_Edit_Code_Typed_Flowcode_Name)
  },
  Flowcode_Management_Edit_Code_Clicked_Transfer_Code(): void {
    const { Flowcode_Management_Edit_Code_Clicked_Transfer_Code } = eventNames
    fireHeapEvent(Flowcode_Management_Edit_Code_Clicked_Transfer_Code)
  },
  Flowcode_Management_Edit_Code_Clicked(args: { variant: string }): void {
    const { Flowcode_Management_Edit_Code_Clicked } = eventNames
    fireHeapEvent(Flowcode_Management_Edit_Code_Clicked, args)
  },
  Revenue_LoggedInGenerator_Clicked_CreateAndDownloadButton(origin?: string): void {
    const { Revenue_LoggedInGenerator_Clicked_CreateAndDownloadButton } = eventNames
    fireHeapEvent(Revenue_LoggedInGenerator_Clicked_CreateAndDownloadButton, {
      ...(origin && {
        origin
      })
    })
  },
  ProTrialModal_Clicked_PrimaryButton({
    label,
    destination
  }: {
    label: string
    destination: string
  }): void {
    fireHeapEvent(eventNames.ProTrialModal_Clicked_PrimaryButton, { label, destination })
  },
  ProTrialModal_Clicked_SecondaryButton({
    label,
    destination
  }: {
    label: string
    destination: string
  }): void {
    fireHeapEvent(eventNames.ProTrialModal_Clicked_SecondaryButton, { label, destination })
  }
}
export default events
